import Modal from '../../Unknown/Modal'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { Box, Button, IconButton, TextField } from '@mui/material'
import translate from '../../../i18n/translate'
import DeleteIcon from '@mui/icons-material/Delete';
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { Formik, Form, FieldArray, getIn } from 'formik'
import { FIELDS, useValidationSchema } from './useValidationSchema'
import { eventApi } from '../../../modules/services/eventService'

type EventCrossQRScannerModalProps = {
  event: {
   id: number; 
  }
}

export default function EventCrossQRScannerModal({
  event,
}: EventCrossQRScannerModalProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { toggleEventCrossQRScannerModal } = modalsSlice.actions
  const visible = useAppSelector(state => state.modalsReducer.eventCrossQRScanner)

  const { data, isLoading } = eventApi.useGetEventScannerCrossListQuery({ eventId: event?.id }, { skip: !event?.id, refetchOnMountOrArgChange: true })
  const [updateEventRelatedLinks, { isLoading: isUpdateLoading }] = eventApi.useUpdateEventScannerCrossListMutation()
  const handleSubmit = async (values: any) => {
    try {
      const { data: resData }: any = await updateEventRelatedLinks({ eventId: event?.id, relatedEventLinks: values?.relatedEventLinks || [] });

      if (resData?.message) {
        toast.error(resData?.message)
      } else {
        dispatch(toggleEventCrossQRScannerModal())
        toast.success(translate('relatedEventsSuccessUpdated'))
      }
    } catch (error) {}
  }

  const onCloseModal = () => {
    dispatch(toggleEventCrossQRScannerModal())
  }

  const validationSchema = useValidationSchema()

  return (
    <Modal
      title={translate('eventCrossQRScannerModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '500px' }}
      setVisible={onCloseModal}
    >
      <Formik
        initialValues={{
          [FIELDS.RELATED_EVENT_LINKS]: isLoading ? [] : data?.map((i: any) => i?.link || '') || [],
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ errors, values, touched, handleBlur, handleChange, isValid }) => {
          return (
            <Form>
              <Box>
                <FieldArray name={FIELDS.RELATED_EVENT_LINKS}>
                  {({ push, remove }) => (
                    <Box display='flex' flexDirection='column' padding={2} gap={1.5}>
                      {(values as any)?.[FIELDS.RELATED_EVENT_LINKS].map((e: string, index: number) => {
                        const fieldName = `${FIELDS.RELATED_EVENT_LINKS}.${index}`

                        const value = getIn(values, fieldName);
                        const touch = getIn(touched, fieldName);
                        const error = getIn(errors, fieldName);

                        return (
                          <Box display='flex' gap={1} key={`event-scanner-${index}`}>
                            <TextField
                              name={fieldName}
                              value={value}
                              error={touch && Boolean(error)}
                              helperText={touch && error}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              variant='outlined'
                              color='warning'
                              disabled={isLoading}
                              fullWidth
                              label={intl.formatMessage({
                                id: 'relatedEventLink'
                              })}
                              placeholder={intl.formatMessage({
                                id: 'relatedEventLink'
                              })}
                              dir='ltr'
                              sx={{
                                '.MuiInputBase-root.MuiInput-root:before': {
                                  borderBottom: '0px'
                                },
                                '.MuiInputBase-root.MuiInput-root:hover': {
                                  borderBottom: '0px'
                                }
                              }}
                            />
                            <IconButton sx={{ height: 40, marginTop: 1 }} color='error' onClick={() => remove(index)}><DeleteIcon /></IconButton>
                          </Box>
                        )
                      })}
                      <Button
                        size='large'
                        variant='outlined'
                        color='warning'
                        fullWidth
                        disabled={(values as any)?.[FIELDS.RELATED_EVENT_LINKS]?.length >= 10}
                        onClick={() => push('')}
                      >
                        {translate('addRelatedScannerEvent')}
                      </Button>
                    </Box>
                  )}
                </FieldArray>
                <Button
                  size='large'
                  variant='contained'
                  color='warning'
                  type='submit'
                  fullWidth
                  disabled={isLoading || !isValid || isUpdateLoading}
                >
                  {translate('updateRelatedScannerEvent')}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
